import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Users, Trophy, GraduationCap, PartyPopper, Clock, MapPin, Phone, Mail } from 'lucide-react';

const YOUTUBE_VIDEO_ID = 'CuezEJ3YEEQ';
const YOUTUBE_WATCH_URL = `https://youtu.be/${YOUTUBE_VIDEO_ID}`;
const YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_VIDEO_ID}&controls=0&modestbranding=1&rel=0&playsinline=1`;
const YOUTUBE_THUMBNAIL_URL = `https://i.ytimg.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`;

const Home = () => {
  useEffect(() => {
    document.title = 'Indoor Golf in Bay Village, OH | Pin High Golf & Lounge';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Indoor golf simulators in Bay Village on Cleveland's Westside. Foresight tech, PGA lessons, leagues, private events, and a full sports bar. Book your bay today.");
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = "Indoor golf simulators in Bay Village on Cleveland's Westside. Foresight tech, PGA lessons, leagues, private events, and a full sports bar. Book your bay today.";
      document.head.appendChild(meta);
    }

    // Add Video Schema markup for hero video
    const videoSchema = {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": "Pin High Golf & Lounge - Indoor Golf Experience",
      "description": "Experience indoor golf at Pin High Golf & Lounge in Bay Village, Ohio. State-of-the-art Foresight simulators, premium lounge atmosphere, and year-round play.",
      "thumbnailUrl": YOUTUBE_THUMBNAIL_URL,
      "contentUrl": YOUTUBE_WATCH_URL,
      "embedUrl": YOUTUBE_EMBED_URL,
      "publisher": {
        "@type": "Organization",
        "name": "Pin High Golf & Lounge",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pinhighbay.com/PinHighLogo_NoTagline.png"
        }
      }
    };

    // Add schema to head
    let script = document.querySelector('#video-schema');
    if (!script) {
      script = document.createElement('script');
      script.id = 'video-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(videoSchema);

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/');
    updateSocialMeta({
      title: 'Indoor Golf in Bay Village, OH | Pin High Golf & Lounge',
      description: "Indoor golf simulators in Bay Village on Cleveland's Westside. Foresight tech, PGA lessons, leagues, private events, and a full sports bar. Book your bay today.",
      path: '/',
    });

    return () => {
      const existingScript = document.querySelector('#video-schema');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <iframe
            src={YOUTUBE_EMBED_URL}
            title="Pin High Golf & Lounge Hero Video"
            className="w-full h-full object-cover pointer-events-none"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Indoor Golf Simulators,<br />
            <span className="text-green-400">Year-Round Play</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
            Bay Village's premier golf simulator lounge featuring Foresight technology, PGA instruction, leagues, and private events
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105"
            >
              <a href="https://app.whoosh.io/patron/club/pin-high-golf-lounge/agenda/pin-high/today" target="_blank" rel="noopener noreferrer">
                Book Your Bay Now
              </a>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6 transition-all duration-300"
            >
              <Link to="/memberships">Explore Memberships</Link>
            </Button>
          </div>
          
          {/* Quick Info Bar */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-green-400" />
              <span>27229 Wolf Rd, Bay Village, OH</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              <span>Open 7 Days a Week</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-green-400" />
              <span>(440) 455-1078</span>
            </div>
          </div>
        </div>
      </section>

      {/* Leagues promo */}
      <section className="relative overflow-hidden border-y-4 border-green-400 bg-gradient-to-br from-gray-950 via-green-950 to-green-800 text-white py-12 md:py-16">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-500/20 via-transparent to-transparent" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-500/20 ring-2 ring-green-400/60 mb-4">
            <Trophy className="w-8 h-8 text-green-300" aria-hidden />
          </div>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-green-300 mb-2">
            New this season
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-3 tracking-tight">
            Pin High Leagues Are Here!
          </h2>
          <p className="text-lg sm:text-xl text-green-50/95 mb-2 font-medium">
            Fun · Competitive · Weather-proof
          </p>
          <p className="text-base sm:text-lg text-green-200/95 mb-6 font-medium">
            Starting November 9th
          </p>
          <p className="text-base sm:text-lg text-gray-200 mb-8 max-w-xl mx-auto leading-relaxed">
            Quick 9-hole mid-week rounds or full 18-hole weekend play—there&apos;s a league for every schedule and skill level.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-green-500 hover:bg-green-400 text-gray-950 font-semibold text-base px-8 py-6 shadow-lg shadow-green-900/40 ring-2 ring-white/10"
          >
            <Link to="/leagues">Join Now</Link>
          </Button>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              A New Way to Play, Connect, and Unwind
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built in the former Bay Lanes space, Pin High carries forward the spirit of community and fun that made this location a Bay Village favorite for generations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Book a Bay</h3>
                <p className="text-gray-600 mb-4">
                  Reserve your simulator bay by the hour. Play famous courses from around the world on Foresight technology.
                </p>
                <Button asChild variant="link" className="text-green-600 hover:text-green-700">
                  <Link to="/pricing">View Rates →</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Join a League</h3>
                <p className="text-gray-600 mb-4">
                  Compete with friends in weekly leagues. Next season starts November 9th—track stats, improve your game, and enjoy the camaraderie.
                </p>
                <Button asChild variant="link" className="text-green-600 hover:text-green-700">
                  <Link to="/leagues">Learn More →</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Pro Lessons</h3>
                <p className="text-gray-600 mb-4">
                  Elevate your game with PGA-certified instructors using advanced swing analysis and Foresight data.
                </p>
                <Button asChild variant="link" className="text-green-600 hover:text-green-700">
                  <Link to="/lessons">Schedule Now →</Link>
                </Button>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PartyPopper className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Private Events</h3>
                <p className="text-gray-600 mb-4">
                  Host corporate outings, birthday parties, and team events in our dedicated private party space.
                </p>
                <Button asChild variant="link" className="text-green-600 hover:text-green-700">
                  <Link to="/events">Plan Your Event →</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Membership Highlight Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="/about-single.jpg"
                alt="Golf simulator at Pin High Golf & Lounge"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                Unlock Unlimited Access
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Our membership tiers are designed to fit every kind of golfer—from casual swing-seekers to dedicated competitors.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Priority bay access during peak hours</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Exclusive member rates and discounts</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Zero weather dependence—play year-round</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Access to members-only leagues and tournaments</span>
                </li>
              </ul>
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300">
                <Link to="/memberships">View Membership Options</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Leagues Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Trophy className="w-16 h-16 text-green-400 mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Join a League
              </h2>
              <p className="text-lg font-semibold text-green-400 mb-4">
                Next season starts November 9th
              </p>
              <p className="text-xl text-gray-300 mb-6">
                Good game. Better company. Our leagues combine friendly competition with community spirit—track your stats, improve your game, and enjoy regular social golf.
              </p>
              <p className="text-lg text-gray-400 mb-8">
                Weekly and seasonal formats, team or solo play, and prizes await. No matter your level, you'll find your place on the leaderboard at Pin High.
              </p>
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300">
                <Link to="/leagues">Join a League</Link>
              </Button>
            </div>
            <div>
              <img
                src="/leagues-hero.jpg"
                alt="League play experience at Pin High Golf & Lounge"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lessons Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="/lessons-hero.jpg"
                alt="Lessons with a pro at Pin High Golf & Lounge"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
              />
            </div>
            <div className="order-1 md:order-2">
              <GraduationCap className="w-16 h-16 text-green-600 mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
                Lessons With a Pro
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Sharpen your skills with instruction from PGA-certified professionals in a cutting-edge indoor environment.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Personalized one-on-one coaching sessions</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Advanced swing analysis using Foresight technology</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Custom club-fitting services available</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-sm">✓</span>
                  </div>
                  <span className="text-gray-700">Performance programs to help you play better and hit farther</span>
                </li>
              </ul>
              <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300">
                <Link to="/lessons">Schedule With a Pro</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              What Our Members Say
            </h2>
            <p className="text-xl text-gray-600">
              Join hundreds of satisfied golfers in Bay Village and beyond
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "Pin High has completely changed my winter golf game. The Foresight simulators are incredible, and the atmosphere is perfect for hanging out with friends."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    MJ
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Mike J.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "Best corporate event venue in the area. We hosted our team outing here and everyone had a blast. The staff made everything seamless."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    SC
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Sarah C.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "The lessons with their pro completely transformed my swing. Worth every penny."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    DP
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">David P.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Tee Up?
          </h2>
          <p className="text-xl mb-8">
            Visit our booking portal or contact us for tour, membership info, and upcoming events. At Pin High Golf & Lounge, the game never stops—let's play.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-green-600 hover:bg-gray-100 text-lg px-8 py-6 transition-all duration-300 transform hover:scale-105"
            >
              <a href="https://app.whoosh.io/patron/club/pin-high-golf-lounge/agenda/pin-high/today" target="_blank" rel="noopener noreferrer">
                Book Your Bay
              </a>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 text-lg px-8 py-6 transition-all duration-300"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;